import { usePassportProvider } from "@/context";
import { useRewardsShare } from "@/hooks/useRewardsShare";
import { trackEvent } from "@analytics";
import { Button, Stack } from "@biom3/react";
import { useMemo, useState } from "react";
import { SocialShareButtons } from "../SocialShareButtons";
import { RedeemAndClaim } from "./Redemption/RedeemAndClaim";
import { RedemptionInfo } from "./RedemptionInfo";
import { RedemptionRewards } from "./RedemptionRewards";
import { RuleModal } from "./RuleModal";
import { useWeeklyRewards } from "./WeeklyRewardsProvider";

export function RedemptionPanel() {
  const {
    sweepstake,
    currentPeriod,
    entryFee,
    latestClaimedTicket,
    refreshSweepstake,
    refreshTickets,
    redeemTicket,
    hasFreeEntry,
    hasEnoughGems,
    isJustClaimedReward,
    ticketUnlockDate,
    setIsJustClaimedReward,
    hasTicketUpgrade,
    sweepstakeTickets,
    predictedStats,
  } = useWeeklyRewards();

  const isLiveProgressKey = sweepstake?.current?.isLiveProgressKey;

  const { passportState } = usePassportProvider();
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);

  const { shareText, shareUrl, trackingExtras, shouldShowShareButtons } =
    useRewardsShare();

  const onOpenRulesModal = () => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "OpenRules",
        section: "RewardsRedemption",
        userJourney: "Rewards",
      },
    });
    setIsRuleModalOpen(true);
  };

  const handleCloseRuleModal = () => {
    trackEvent({
      component: "Button",
      action: "Pressed",
      extras: {
        control: "CloseRules",
        section: "RewardsRedemptionPanel",
        userJourney: "Rewards",
      },
    });
    setIsRuleModalOpen(false);
  };

  const nextWeeklyRunDate = sweepstake?.next?.startDate
    ? new Date(sweepstake?.next.startDate)
    : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

  const endRedeemDate = sweepstake?.current
    ? new Date(sweepstake?.current.endDate)
    : sweepstake?.previous
      ? new Date(sweepstake?.previous.endDate)
      : new Date();

  const unlockDate = sweepstake?.current?.calculationPeriodEndDate
    ? new Date(sweepstake?.current.calculationPeriodEndDate)
    : sweepstake?.previous?.calculationPeriodEndDate
      ? new Date(sweepstake?.previous?.calculationPeriodEndDate)
      : new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);

  const participants = sweepstake?.current
    ? Number(sweepstake?.current.participantsAmount || 0).toLocaleString()
    : sweepstake?.previous
      ? Number(sweepstake?.previous.participantsAmount || 0).toLocaleString()
      : "0";

  const hideParticipants = useMemo(() => {
    const hasKeyForCurrentSweepstake =
      predictedStats?.hasKeyForSweepstake === true;
    const hasRelevantTicket =
      sweepstakeTickets?.some(
        (ticket) => ticket.status === "claimable" || ticket.status === "locked",
      ) ?? false;
    return !hasKeyForCurrentSweepstake && hasRelevantTicket;
  }, [predictedStats?.hasKeyForSweepstake, sweepstakeTickets]);

  // Check if we're in idle state
  const isWaitingForNext = currentPeriod === "IDLE";

  const onCountdownEnd = () => {
    refreshTickets(undefined);
    refreshSweepstake(undefined);
  };

  return (
    <Stack
      direction={{ default: "column-reverse", medium: "column" }}
      sx={{
        position: "relative",
        width: "100%",
        borderRadius: "base.borderRadius.large",
        overflow: "hidden",
        bg: "base.color.background.surface.secondary",
      }}
      gap={{ default: "base.spacing.x2", large: "base.spacing.x8" }}
    >
      {(currentPeriod === "REDEEM" ||
        currentPeriod === "CALCULATE" ||
        isWaitingForNext) &&
        !isJustClaimedReward && (
          <Stack
            direction={{ default: "column", large: "row" }}
            sx={{
              display: "flex",
              alignItems: "center",
              maxWidth: "850px",
            }}
          >
            {!isJustClaimedReward && (
              <RedemptionInfo
                type="countdown"
                icon="Countdown"
                title={
                  isWaitingForNext
                    ? "Next weekly rewards in:"
                    : currentPeriod === "REDEEM"
                      ? "Time left to redeem:"
                      : "Available to unlock in:"
                }
                countdownValue={
                  isWaitingForNext
                    ? nextWeeklyRunDate
                    : currentPeriod === "REDEEM"
                      ? endRedeemDate
                      : isLiveProgressKey
                        ? new Date(ticketUnlockDate || unlockDate)
                        : unlockDate
                }
                onCountdownEnd={onCountdownEnd}
              />
            )}
            {!hideParticipants && participants && (
              <RedemptionInfo
                type="stats"
                icon="People"
                title="Participants"
                value={participants}
              />
            )}
          </Stack>
        )}

      {currentPeriod !== "THANKS" && !isJustClaimedReward && (
        <Stack
          direction={["column", "column", "column", "row"]}
          sx={{
            display: "flex",
            gap: "base.spacing.x2",
          }}
        >
          {!passportState.ready ? (
            <Button variant="tertiary" size="large" disabled>
              <Button.Icon icon="Loading" />
            </Button>
          ) : passportState.authenticated &&
            currentPeriod ===
              "CALCULATE" ? null : passportState.authenticated &&
            passportState.zkEvmRegistered &&
            !isWaitingForNext ? (
            <RedeemAndClaim onOpenRulesModal={onOpenRulesModal} />
          ) : null}
          <Stack direction="row">
            <Button
              size="large"
              variant="secondary"
              onClick={onOpenRulesModal}
              sx={{
                flex: 1,
              }}
            >
              Rules
              <Button.Icon icon="Document" />
            </Button>
            {shouldShowShareButtons && (
              <SocialShareButtons
                shareText={shareText}
                shareUrl={shareUrl}
                trackingSection="RewardsRedemption"
                trackingExtras={trackingExtras}
              />
            )}
          </Stack>
        </Stack>
      )}
      {(currentPeriod === "THANKS" || isJustClaimedReward) && (
        <>
          {shouldShowShareButtons && (
            <SocialShareButtons
              shareText={shareText}
              shareUrl={shareUrl}
              trackingSection="RewardsRedemption"
              trackingExtras={trackingExtras}
              sx={{
                marginTop: "base.spacing.x1",
                display: {
                  default: "flex",
                  medium: "none",
                },
              }}
            />
          )}
          <RedemptionRewards
            claimedRewards={latestClaimedTicket?.metadata}
            showHistory
          />
          <Stack direction="row" alignItems="center">
            {/** Case when i have recently claimed reward **/}
            {isJustClaimedReward && sweepstake?.current ? (
              <Button
                size="large"
                sx={{ width: { large: "212px", default: "auto" } }}
                onClick={() => setIsJustClaimedReward(false)}
              >
                Claim a new key
              </Button>
            ) : (
              <RedemptionInfo
                type="countdown"
                icon="Countdown"
                title="Next weekly draw in:"
                countdownValue={nextWeeklyRunDate}
                onCountdownEnd={onCountdownEnd}
                sx={{
                  maxWidth: "315px",
                }}
              />
            )}
            {shouldShowShareButtons && (
              <SocialShareButtons
                shareText={shareText}
                shareUrl={shareUrl}
                trackingSection="RewardsRedemption"
                trackingExtras={trackingExtras}
                sx={{
                  display: {
                    default: "none",
                    medium: "inherit",
                  },
                }}
              />
            )}
          </Stack>
        </>
      )}

      <RuleModal
        isOpen={isRuleModalOpen}
        onClose={handleCloseRuleModal}
        gemsEntryAmount={entryFee}
        showGetKeyButton={currentPeriod === "REDEEM"}
        onRedeemTicket={redeemTicket}
        hasFreeEntry={hasFreeEntry}
        hasEnoughGems={hasEnoughGems}
        hasTicketUpgrade={hasTicketUpgrade}
      />
    </Stack>
  );
}
